import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';

import { platformSaasTimelineService } from '../../services/api';

const { Title, Text } = Typography;

const EVENT_TYPE_OPTIONS = [
  { label: 'SUBSCRIPTION_CREATED', value: 'SUBSCRIPTION_CREATED' },
  { label: 'SUBSCRIPTION_TRANSITION_AUDIT', value: 'SUBSCRIPTION_TRANSITION_AUDIT' },
  { label: 'CHANGE_REQUEST_CREATED', value: 'CHANGE_REQUEST_CREATED' },
  { label: 'CHANGE_REQUEST_APPROVED', value: 'CHANGE_REQUEST_APPROVED' },
  { label: 'CHANGE_REQUEST_REJECTED', value: 'CHANGE_REQUEST_REJECTED' },
  { label: 'WEBHOOK_EVENT', value: 'WEBHOOK_EVENT' },
  { label: 'MATERIALIZER_EVENT', value: 'MATERIALIZER_EVENT' },
];

const SOURCE_OPTIONS = [
  { label: 'subscription', value: 'subscription' },
  { label: 'self_service', value: 'self_service' },
  { label: 'platform_admin', value: 'platform_admin' },
  { label: 'webhook_bridge', value: 'webhook_bridge' },
  { label: 'lifecycle_materializer', value: 'lifecycle_materializer' },
];

const severityColor = {
  info: 'blue',
  warning: 'orange',
  critical: 'red',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const PlatformTenantTimelinePage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [appliedTenantId, setAppliedTenantId] = useState('');

  const loadTimeline = async (values) => {
    const tenantId = (values.tenant_id || '').trim();
    if (!tenantId) {
      setError('Informe o tenant_id para buscar a timeline.');
      setItems([]);
      return;
    }

    setLoading(true);
    setError('');

    const params = {
      from_date: values.period?.[0] ? values.period[0].startOf('day').toISOString() : undefined,
      to_date: values.period?.[1] ? values.period[1].endOf('day').toISOString() : undefined,
      event_type: values.event_type || undefined,
      source: values.source || undefined,
      limit: values.limit || 200,
    };

    try {
      const response = await platformSaasTimelineService.listTenantTimeline(tenantId, params);
      setItems(Array.isArray(response?.data) ? response.data : []);
      setAppliedTenantId(tenantId);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Nao foi possivel carregar a timeline do tenant.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Data/Hora',
        dataIndex: 'occurred_at',
        key: 'occurred_at',
        render: (value) => formatDateTime(value),
      },
      {
        title: 'Evento',
        dataIndex: 'event_type',
        key: 'event_type',
        render: (value) => <Tag>{value || '-'}</Tag>,
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        render: (value) => value || '-',
      },
      {
        title: 'Severidade',
        dataIndex: 'severity',
        key: 'severity',
        render: (value) => <Tag color={severityColor[value] || 'default'}>{value || '-'}</Tag>,
      },
      {
        title: 'Titulo',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Descricao',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Correlation',
        dataIndex: 'correlation_id',
        key: 'correlation_id',
        render: (value) => <Text code>{value || '-'}</Text>,
      },
      {
        title: 'Subscription',
        dataIndex: 'subscription_id',
        key: 'subscription_id',
        render: (value) => <Text code>{value || '-'}</Text>,
      },
      {
        title: 'Version',
        dataIndex: 'transition_version',
        key: 'transition_version',
        render: (value) => (Number.isInteger(value) ? value : '-'),
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Timeline SaaS por Tenant</Title>
      <Text type="secondary">Visao operacional consolidada de eventos de assinatura para suporte e auditoria.</Text>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16, marginBottom: 16 }}
        message="Consulta somente leitura"
        description="Esta tela consolida eventos historicos e nao executa upgrade, downgrade ou mutacao de assinatura."
      />

      {error ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Falha ao consultar timeline" description={error} />
      ) : null}

      <Card size="small" title="Filtros" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={loadTimeline} initialValues={{ limit: 200 }}>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item
              name="tenant_id"
              label="Tenant ID"
              style={{ minWidth: 280 }}
              rules={[{ required: true, message: 'Informe o tenant_id' }]}
            >
              <Input placeholder="Tenant ID" />
            </Form.Item>
            <Form.Item name="event_type" label="Event type" style={{ minWidth: 260 }}>
              <Select allowClear options={EVENT_TYPE_OPTIONS} placeholder="Todos" />
            </Form.Item>
            <Form.Item name="source" label="Source" style={{ minWidth: 240 }}>
              <Select allowClear options={SOURCE_OPTIONS} placeholder="Todos" />
            </Form.Item>
            <Form.Item name="period" label="Periodo" style={{ minWidth: 320 }}>
              <DatePicker.RangePicker showTime />
            </Form.Item>
            <Form.Item name="limit" label="Limite" style={{ width: 140 }}>
              <Input type="number" min={1} max={500} />
            </Form.Item>
          </Space>
          <Space>
            <Button type="primary" htmlType="submit">Buscar timeline</Button>
            <Button
              onClick={() => {
                form.resetFields();
                setItems([]);
                setAppliedTenantId('');
                setError('');
              }}
            >
              Limpar
            </Button>
          </Space>
        </Form>
      </Card>

      <Card
        size="small"
        title={appliedTenantId ? `Eventos do tenant ${appliedTenantId}` : 'Eventos'}
        extra={<Text type="secondary">{items.length} registro(s)</Text>}
      >
        {loading ? (
          <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" tip="Consultando timeline..." />
          </div>
        ) : (
          <Table
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={items}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 1400 }}
          />
        )}
      </Card>
    </div>
  );
};

export default PlatformTenantTimelinePage;
